import MyContext from "../contexts/AppContext";
import { BiHome, BiArrowBack, BiPlusCircle, BiFile, BiPrinter, BiTime } from "react-icons/bi";
import {
  BreadcrumbActionsContext,
  type BreadcrumbActionsConfig,
  type BreadcrumbAction,
} from "../contexts/BreadcrumbActionsContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getRotaVoltar, isIdSegment } from "../utils/rotaVoltar";
import { useContext } from "react";
import { ModuloSistema } from "../constants/modulos-sistema.enum";

// Mapeia os segmentos da rota para nomes mais amigáveis
const breadcrumbNameMap: Record<string, string> = {
  acoes: "Ações",
  ajuda: "Ajuda",
  atuacoes: 'Atuações',
  colaboradores: "Colaboradores",
  comarcas: 'Comarcas',
  defensor: 'Defensor',
  distrito: 'Distrito',
  estagiario: 'Estagiários',
  feriados: 'Bloqueios',
  folha_frequencia: 'Folhas de Frequência',
  instituicoes: 'Instituições',
  movimentacoes: 'Movimentações',
  nucleo: 'Núcleo',
  papeis: 'Papéis',
  relatorios: 'Relatórios',
  sessoes: 'Sessões',
  usuario: 'Usuários',
  varas: 'Varas',
  vinculos: 'Vínculos',
  vinculos_colaborador: 'Vínculos',
  vinculos_defensor: 'Vínculos',
  vinculos_estagio: 'Vínculos',
  'gestao-arquivos': 'Gestão de Arquivos',
  'processo-seletivo': 'Processos seletivos',
  analises: 'Análises',
};

// Segmentos que devem redirecionar para /vinculos ao invés do caminho original
const vinculosRedirectSegments = ['vinculos_defensor', 'vinculos_colaborador', 'vinculos_estagio'];

function getLabel(segment: string) {
  return breadcrumbNameMap[segment] || segment;
}

// Ordem fixa: Ação Primária, Ação Secundária, Imprimir, Histórico
const SLOT_ORDER: (keyof BreadcrumbActionsConfig)[] = ['primary', 'secondary', 'print', 'history'];
const SLOT_ICONS = {
  primary: BiPlusCircle,
  secondary: BiFile,
  print: BiPrinter,
  history: BiTime,
};

function renderActionButton(
  slot: keyof BreadcrumbActionsConfig,
  action: BreadcrumbAction,
  index: number
) {
  const Icon = SLOT_ICONS[slot];
  const btnClass = action.variant === 'primary' ? 'btn btn-primary btn-sm' : 'btn btn-outline-secondary btn-sm';
  const content = (
    <>
      <Icon size={18} className="me-1" aria-hidden />
      {action.label}
    </>
  );

  if (action.dropdownOptions && action.dropdownOptions.length > 0) {
    const dropdownId = `breadcrumb-dropdown-${slot}-${index}`;
    return (
      <div key={index} className="dropdown d-inline-block">
        <button
          type="button"
          className={`d-inline-flex align-items-center dropdown-toggle ${btnClass}`}
          id={dropdownId}
          data-bs-toggle="dropdown"
          aria-expanded="false"
          aria-haspopup="true"
        >
          {content}
        </button>
        <ul className="dropdown-menu dropdown-menu-end" aria-labelledby={dropdownId}>
          {action.dropdownOptions.map((opt, i) => (
            <li key={i}>
              <button
                type="button"
                className="dropdown-item"
                onClick={(e) => {
                  e.preventDefault();
                  opt.onClick();
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (action.href) {
    return (
      <Link
        key={index}
        to={action.href}
        className={`d-inline-flex align-items-center ${btnClass}`}
        onClick={action.onClick}
      >
        {content}
      </Link>
    );
  }
  return (
    <button
      key={index}
      type="button"
      className={`d-inline-flex align-items-center ${btnClass}`}
      onClick={(e) => {
        e.preventDefault();
        action.onClick();
      }}
    >
      {content}
    </button>
  );
}

export default function Breadcrumb() {
  const context = useContext(MyContext);
  const location = useLocation();
  const navigate = useNavigate();
  const actionsContext = useContext(BreadcrumbActionsContext);

  const actions: BreadcrumbActionsConfig = actionsContext?.actions ?? {};

  if (context) {
    if (!context.state.navbar) {
      return <></>;
    }
    if (
      context.state.modulo === ModuloSistema.RelatoriosAtividades &&
      !context.state.username?.trim()
    ) {
      return <></>;
    }
  }

  const pathnames = location.pathname.split("/").filter((x) => x);
  const isPaginaInicial = pathnames.length <= 0 || location.pathname === '/' || location.pathname === '/home';
  const rotaVoltar = getRotaVoltar(location.pathname);
  const mostrarBotaoVoltar = pathnames.length > 0;

  const hasAnyAction = SLOT_ORDER.some((slot) => actions[slot]);

  // Se não há ações e não é para mostrar breadcrumb/voltar, não renderiza nada
  if (isPaginaInicial && !hasAnyAction && !mostrarBotaoVoltar) {
    return <></>;
  }

  return (
    <div className="navbar navbar-expand-lg navbar-light bg-light border-bottom" style={{ minHeight: '50px' }}>
      <div className="container d-flex align-items-center justify-content-between w-100">
        <div className="d-flex align-items-center">
          {/* Breadcrumb - não mostra na página inicial */}
          {!isPaginaInicial && (
            <nav aria-label="breadcrumb" className="d-inline-flex align-items-center">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/" className="text-decoration-none">
                    <BiHome size={16} className="me-1" />
                  </Link>
                </li>
                {pathnames.map((value, index) => {
                  const nextSegment = pathnames[index + 1];
                  const isLast = index === pathnames.length - 1;

                  let to = `/${pathnames.slice(0, index + 1).join("/")}`;
                  if (isIdSegment(nextSegment)) {
                    to = `/${pathnames.slice(0, index + 2).join("/")}`;
                  }

                  // Se o segmento é um dos tipos de vínculo, redireciona para /vinculos
                  if (vinculosRedirectSegments.includes(value) && !isLast) {
                    to = '/vinculos';
                  }

                  if (isIdSegment(value))
                    return null;

                  return (
                    <li
                      key={to}
                      className={`breadcrumb-item ${isLast ? "active" : ""}`}
                      aria-current={isLast ? "page" : undefined}
                    >
                      {isLast ? (
                        getLabel(value)
                      ) : (
                        <Link to={to} className="text-decoration-none">{getLabel(value)}</Link>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
          )}
          {/* Botão Voltar - após o último breadcrumb (state fromVoltar para 404 redirecionar um nível) */}
          {mostrarBotaoVoltar && (
            <>
              <span className="mx-2 text-muted" aria-hidden>/</span>
              <Link
                to={rotaVoltar}
                title="Voltar"
                className="text-decoration-none d-print-none"
                state={{ fromVoltar: true }}
              >
                <BiArrowBack size={20} />
              </Link>
            </>
          )}
        </div>
        <div className="d-flex align-items-center ms-auto gap-2">
          {/* Voltar à página anterior (histórico do navegador) - sempre presente quando não é página inicial */}
          {mostrarBotaoVoltar && (
            <button
              type="button"
              className="d-inline-flex align-items-center btn btn-outline-secondary btn-sm d-print-none"
              onClick={() => navigate(-1)}
              title="Voltar à página anterior"
            >
              <BiArrowBack size={18} className="me-1" aria-hidden />
              Voltar
            </button>
          )}
          {SLOT_ORDER.map((slot, idx) => {
            const action = actions[slot];
            if (!action) return null;
            return renderActionButton(slot, action, idx);
          })}
        </div>
      </div>
    </div>
  );
}
